import { formatDistanceToNow } from 'date-fns';
import { HistoryIcon, LinkIcon, ZapIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import IfIcon from '@/components/atoms/Icons/IfIcon';
import { ListTooltipContent } from '@/components/atoms/ListTooltipContent/ListTooltipContent';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Version } from '@/shared/types/version';

export type ConnectedRule = { rule_id: string; name: string; desk_id: string };

interface VersionTooltipContentProps {
  version: Version;
  connectedRules?: ConnectedRule[];
}

export const VersionTooltipContent = ({ version, connectedRules }: VersionTooltipContentProps) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const isDraft = version.version === 0;

  const lastUsedText = version.last_used_at
    ? formatDistanceToNow(new Date(version.last_used_at), { addSuffix: true })
    : null;

  const hasRules = !!connectedRules && connectedRules.length > 0;

  return (
    <div className="flex w-60 flex-col">
      <div className="flex items-center justify-between py-1.5 pr-3 pl-2">
        <p className="py-1 pr-1.5 pl-1 text-xs leading-4 font-semibold whitespace-nowrap">
          {isDraft ? t('common.draft') : `${t('common.version')} ${version.version}`}
        </p>
        {lastUsedText && (
          <div className="flex items-center gap-1">
            <HistoryIcon className="size-4 opacity-70" />
            <p className="text-xs leading-5 whitespace-nowrap">{lastUsedText}</p>
          </div>
        )}
      </div>

      {hasRules && (
        <div className="flex flex-col items-center gap-1 px-1.5 pt-1.5 pb-2">
          <div className="flex h-18 w-full items-center justify-center rounded bg-foreground-muted/80">
            <div className="flex items-center gap-0.5 text-foreground">
              <div className="rounded bg-bg-muted p-1 shadow-lg">
                <ZapIcon className="size-4" />
              </div>
              <div className="w-8 border-t border-dashed border-bg-muted" />
              <LinkIcon className="size-4 text-bg-muted" />
              <div className="w-8 border-t border-dashed border-bg-muted" />
              <div className="relative">
                <div className="absolute top-0.75 left-2.75 size-6 rounded-md bg-bg-muted opacity-80 shadow-lg" />
                <div className="absolute top-px left-1.25 size-7 rounded-md bg-bg-muted opacity-80 shadow-lg" />
                <div className="relative rounded-[5px] bg-bg-muted p-[5px] shadow-lg">
                  <IfIcon className="size-5" color="currentColor" />
                </div>
              </div>
            </div>
          </div>
          <p className="w-full px-1 text-xs leading-5 font-medium">
            {t('campaigns.setup.version_linked_to_rules')}
          </p>
        </div>
      )}

      {version.description && (
        <div className="border-t-[0.5px] border-t-stroke-muted-40 px-3 pt-2.5 pb-3">
          <p className="line-clamp-3 text-xs leading-5">{version.description}</p>
        </div>
      )}

      {hasRules && slug && (
        <div className="border-t-[0.5px] border-t-stroke-muted-40">
          <ListTooltipContent
            title=""
            items={connectedRules.map((rule) => ({
              id: rule.rule_id,
              label: rule.name,
              href: `/${slug}/environments/${rule.desk_id}/rules/${rule.rule_id}`,
            }))}
          />
        </div>
      )}
    </div>
  );
};
